import { PipelineConfig, PreloadConfig, WorkerConfig } from './config-types';
import { WorkerGeneratedResult } from './generator-types';
import { logger } from './logger';
import { resourceLoader } from './resource-loader';

/**
 * Worker manager class to handle the initialization and termination of the segmenation
 * web worker. This class is responsible for loading the worker script, resolving the
 * configuration, and managing the worker lifecycle. It also provides methods to
 * preload the worker and terminate it when no longer needed.
 */
export class WorkerManager {
    private worker?: Worker;
    private workerUrl?: string;
    private destroyPromise: Promise<void> | null = null;

    // Event callback registries
    private onInitializedCallbacks: Array<() => void> = [];
    private onGeneratedCallbacks: Array<(data: WorkerGeneratedResult) => void> = [];
    private onDroppedCallbacks: Array<(timestamp: number) => void> = [];
    private onLogCallbacks: Array<(message: string, severity: string) => void> = [];

    /**
     * Get the current worker instance.
     *
     * @returns The current worker instance or undefined if not initialized.
     */
    public getWorker() {
        return this.worker;
    }

    /**
     * Initialize the worker using the provided configuration.
     *
     * @param config - The configuration object.
     * @returns A promise that resolves when the worker is initialized.
     */
    public async initializeWorker(config: WorkerConfig): Promise<Worker> {
        if (typeof Worker === 'undefined') {
            logger.errorAndThrow('Web workers are not supported in this environment');
        }

        if (this.worker) {
            await this.destroyWorker();
        }

        const scriptUri = await resourceLoader.resolveUri(config, config.mask.workerUri);
        const response = await fetch(scriptUri, { cache: 'force-cache' });
        const workerSource = await response.text();

        this.workerUrl = URL.createObjectURL(new Blob([workerSource], { type: 'text/javascript' }));
        this.worker = new Worker(this.workerUrl, { type: 'module' });

        // Attach a single message handler for all events
        this.worker.addEventListener('message', (event: MessageEvent) => {
            if (event.data.type === 'log') {
                const { message, severity } = event.data;
                this.onLogCallbacks.forEach((cb) => cb(message, severity));
            } else if (event.data.status === 'generated') {
                this.onGeneratedCallbacks.forEach((cb) => cb(event.data));
            } else if (event.data.status === 'dropped') {
                this.onDroppedCallbacks.forEach((cb) => cb(event.data.timestamp));
            } else if (event.data.status === 'initialized') {
                this.onInitializedCallbacks.forEach((cb) => cb());
            }
            // 'destroyed' is handled in destroyWorker
        });

        return this.worker;
    }

    /**
     * Destroys the worker gracefully by sending a destroy message and waiting for confirmation.
     *
     * @returns A promise that resolves when the worker is destroyed.
     *          If no worker is available, it resolves immediately.
     */
    public destroyWorker(): Promise<void> {
        // Wait for any ongoing destroy operation to finish
        if (!this.destroyPromise) {
            this.destroyPromise = new Promise<void>((resolve) => {
                const handleMessage = (event: MessageEvent) => {
                    if (event.data.status === 'destroyed') {
                        this.worker?.removeEventListener('message', handleMessage);
                        this.terminateWorker();
                        this.destroyPromise = null;
                        resolve();
                    }
                };

                if (!this.worker) {
                    logger.warn('No worker to destroy');
                    resolve();
                    return;
                }

                this.worker.addEventListener('message', handleMessage);
                this.worker.postMessage({ type: 'destroy' });
            });
        }

        return this.destroyPromise;
    }

    /**
     * Terminate the worker.
     */
    public terminateWorker() {
        if (this.worker) {
            this.worker.terminate();
            this.worker = undefined;
        }
        if (this.workerUrl) {
            URL.revokeObjectURL(this.workerUrl);
            this.workerUrl = undefined;
        }
    }

    /**
     * Preload the worker using the provided configuration.
     *
     * @param config - The configuration object.
     * @returns A promise that resolves when the worker is preloaded.
     */
    public async preloadWorker(config: PreloadConfig) {
        const workerConfig = await this.prepareWorkerConfig(config);
        this.worker = await this.initializeWorker(workerConfig);
        this.worker.postMessage({ type: 'preload', config: workerConfig });
    }

    /**
     * Prepare the worker configuration by resolving the final URLs and paths to send to the
     * worker. This is necessary because we can't send functions to the worker. We also need to
     * remove the assetUrlResolver function from the configuration.
     *
     * @param config - The preload configuration.
     * @returns The worker configuration.
     */
    public async prepareWorkerConfig(config: PreloadConfig) {
        // Resolve and cache the URIs before sending the config to the worker.
        // Future calls to the resolveUri method will use the cache.
        const modelUri = await resourceLoader.resolveUri(config, config.mask.modelUri);
        const workerUri = await resourceLoader.resolveUri(config, config.mask.workerUri);
        const wasmUri = await resourceLoader.resolveUri(config, config.wasmUri);
        const resolvedConfig: PreloadConfig = {
            ...config,
            mask: { ...config.mask, modelUri, workerUri },
            wasmUri,
        };

        // We can't send the assetUrlResolver function to the worker so we remove it from the config.
        const workerConfig = { ...resolvedConfig, assetUrlResolver: undefined };

        // This is a workaround to ensure the worker configuration can be sent to the worker.
        return JSON.parse(JSON.stringify(workerConfig));
    }

    /**
     * Register a callback for when the worker is initialized.
     */
    public onInitialized(callback: () => void) {
        this.onInitializedCallbacks.push(callback);
    }

    /**
     * Register a callback for when the worker generates output.
     *
     * @param callback - The callback function to be called with the generated data.
     * @param data - The generated data from the worker.
     * @param data.mask - The generated mask.
     * @param data.facesAndLandmarks - The detected faces and landmarks.
     * @param data.gesture - The detected gesture.
     * @param data.motion - The detected motion.
     */
    public onGenerated(callback: (data: WorkerGeneratedResult) => void) {
        this.onGeneratedCallbacks.push(callback);
    }

    /**
     * Register a callback for when the worker drops a frame.
     * This is useful for handling cases where the worker cannot keep up with the input frame rate.
     * The callback will be called with the timestamp of the dropped frame.
     *
     * @param callback - The callback function to be called with the timestamp of the dropped frame.
     */
    public onDropped(callback: (timestamp: number) => void) {
        this.onDroppedCallbacks.push(callback);
    }

    /**
     * Register a callback for log messages from the worker.
     *
     * @param callback - The callback function to be called with the log message and severity.
     * @param severity - The severity of the log message (e.g., 'info', 'warn', 'error').
     */
    public onLog(callback: (message: string, severity: string) => void) {
        this.onLogCallbacks.push(callback);
    }

    /**
     * Send an init message to the worker.
     *
     * @param config - The configuration object to initialize the worker.
     * @param config.baseUrl - The base URL for loading assets.
     * @param config.input - The input configuration for the worker.
     * @param config.mask - The mask configuration for the worker.
     * @param config.wasmUri - The URI for the WebAssembly module.
     * @param config.workerUri - The URI for the worker script.
     * @param config.assetUrlResolver - The function to resolve asset URLs.
     */
    public sendInit(config: PipelineConfig) {
        if (!this.worker) {
            logger.errorAndThrow('Worker is not initialized, cannot send init');
        }
        this.worker.postMessage({ type: 'init', config });
    }

    /**
     * Send a generate message to the worker.
     *
     * @param input - The input image data as a Uint8ClampedArray.
     * @param timestamp - The timestamp of the input image.
     */
    public sendGenerate(input: Uint8ClampedArray, timestamp: number) {
        if (!this.worker) {
            logger.errorAndThrow('Worker is not initialized, cannot send generate');
        }
        this.worker.postMessage(
            {
                type: 'generate',
                image: input,
                timestamp,
            },
            [input.buffer]
        );
    }
}

// Global instance of the shared resource manager.
export const workerManager = new WorkerManager();
